import React, { useState } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import authService from "../services/auth";
import CreditsBadge from "../components/CreditsBadge";
import useCredits from "../hooks/useCredits";
import {
  MenuIcon,
  HomeIcon,
  WidgetIcon,
  SettingsIcon,
  ShieldIcon,
  CreditCardIcon,
} from "../components/NavIcons";
import "./DashboardLayout.css";

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const formatBalance = (balance) => {
  if (balance == null) return "unlimited";
  return String(balance);
};

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const user = authService.getCurrentUser();
  const {
    credits,
    lowBalanceWarnings,
    loading: creditsLoading,
    error: creditsError,
  } = useCredits();
  const [warningDismissed, setWarningDismissed] = useState(false);

  const handleLogout = async () => {
    await authService.logout();
    navigate("/login");
  };

  const isActive = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <div className="dashboard-layout">
      {/* Top Navigation Bar */}
      <header className="dashboard-header">
        <div className="header-left">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
          >
            <MenuIcon />
          </button>
          <h1 className="dashboard-title">Kwibl</h1>
        </div>
        <div className="header-right">
          <CreditsBadge
            credits={credits}
            lowBalanceWarnings={lowBalanceWarnings}
            loading={creditsLoading}
            error={creditsError}
          />
          <div className="user-menu">
            <span className="user-name">
              {user?.username || user?.email || "User"}
            </span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      {!warningDismissed && lowBalanceWarnings.length > 0 && (
        <div className="low-credits-banner">
          <span className="low-credits-banner__text">
            {lowBalanceWarnings.length === 1
              ? `Your ${capitalize(lowBalanceWarnings[0])} credits are running low (${formatBalance(credits?.[lowBalanceWarnings[0]])} remaining).`
              : `Credits running low for: ${lowBalanceWarnings.map((f) => `${capitalize(f)} (${formatBalance(credits?.[f])})`).join(", ")}.`}{" "}
            <Link to="/settings" className="low-credits-banner__link">
              View details
            </Link>
          </span>
          <button
            className="low-credits-banner__dismiss"
            onClick={() => setWarningDismissed(true)}
            aria-label="Dismiss warning"
          >
            &times;
          </button>
        </div>
      )}

      <div className="dashboard-content-wrapper">
        {/* Sidebar */}
        <aside
          className={`dashboard-sidebar ${sidebarOpen ? "open" : "closed"}`}
        >
          <nav className="sidebar-nav">
            <Link
              to="/dashboard"
              className={`nav-item ${isActive("/dashboard") && location.pathname === "/dashboard" ? "active" : ""}`}
            >
              <span className="nav-icon">
                <HomeIcon />
              </span>
              <span className="nav-label">Home</span>
            </Link>

            <Link
              to="/widgets"
              className={`nav-item ${isActive("/widgets") ? "active" : ""}`}
            >
              <span className="nav-icon">
                <WidgetIcon />
              </span>
              <span className="nav-label">My Widgets</span>
            </Link>

            <Link
              to="/settings"
              className={`nav-item ${isActive("/settings") ? "active" : ""}`}
            >
              <span className="nav-icon">
                <SettingsIcon />
              </span>
              <span className="nav-label">Settings</span>
            </Link>

            {user?.role === "admin" && (
              <>
                <Link
                  to="/admin/plans"
                  className={`nav-item ${isActive("/admin/plans") ? "active" : ""}`}
                >
                  <span className="nav-icon">
                    <ShieldIcon />
                  </span>
                  <span className="nav-label">Plans</span>
                </Link>
                <Link
                  to="/admin/credits"
                  className={`nav-item ${isActive("/admin/credits") ? "active" : ""}`}
                >
                  <span className="nav-icon">
                    <CreditCardIcon />
                  </span>
                  <span className="nav-label">Credits</span>
                </Link>
              </>
            )}
          </nav>
          <div className="sidebar-footer">
            © {new Date().getFullYear()} Streamline Small Biz
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="dashboard-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
